import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApp, useAsync } from '../lib/app-context';
import { Async, Badge, Card, Empty, SelectField, TextField } from '../components/ui';
import { buildTree, flatten, levelName, moveSibling, nextSortOrder, descendantIds } from '../lib/tree';
import type { Program, ProgramNode, Session, Uuid } from '../lib/types';

/**
 * Phase → Week → Day, edited in place.
 *
 * Reordering uses explicit move buttons rather than drag-and-drop: it is keyboard
 * accessible, has no drag library, and each press maps to the minimal set of sort_order
 * updates the flat adjacency list needs. Drag would look nicer and be worse to use here.
 */
export function Programme() {
    const { moduleId } = useParams();
    const { data } = useApp();
    const state = useAsync(async () => ({
        program: await data.getProgram(moduleId!),
        nodes: await data.listProgramNodes(moduleId!),
        sessions: await data.listSessions(moduleId!),
    }), [moduleId]);

    return (
        <>
            <div className="page-head">
                <div>
                    <h1>Programme</h1>
                    <p className="muted">
                        The structured path. Group nodes nest freely; a node with a session
                        attached is a trainable day.
                    </p>
                </div>
            </div>

            <Async state={state}>
                {({ program, nodes, sessions }) =>
                    program ? (
                        <Tree program={program} nodes={nodes} sessions={sessions} onChange={state.reload} />
                    ) : (
                        <CreateProgramme moduleId={moduleId!} onCreated={state.reload} />
                    )
                }
            </Async>
        </>
    );
}

function CreateProgramme({ moduleId, onCreated }: { moduleId: Uuid; onCreated: () => void }) {
    const { data } = useApp();
    const [title, setTitle] = useState('Foundations');

    async function create() {
        const root = await data.upsertProgramNode({ module_id: moduleId, title, sort_order: 0 });
        await data.upsertProgram({
            module_id: moduleId, title, summary: '', pacing: 'selfPaced', root_id: root.id,
        });
        onCreated();
    }

    return (
        <Card>
            <h2>No programme yet</h2>
            <p className="muted small" style={{ marginTop: 6 }}>
                Create one to start building the phase / week / day structure.
            </p>
            <div className="row" style={{ marginTop: 12 }}>
                <div style={{ flex: 1, maxWidth: 300 }}>
                    <TextField label="Programme title" value={title} onChange={setTitle} />
                </div>
            </div>
            <button className="btn" style={{ marginTop: 12 }} disabled={!title} onClick={create}>
                Create programme
            </button>
        </Card>
    );
}

function Tree({ program, nodes, sessions, onChange }: {
    program: Program; nodes: ProgramNode[]; sessions: Session[]; onChange: () => void;
}) {
    const { data } = useApp();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const tree = buildTree(nodes, program.root_id);
    const rows = flatten(tree);

    async function run(action: () => Promise<void>) {
        setBusy(true); setError(null);
        try {
            await action();
            onChange();
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    }

    const attachedSessionIds = new Set(nodes.map((n) => n.session_id).filter(Boolean));
    const unattached = sessions.filter((s) => !attachedSessionIds.has(s.id));

    return (
        <>
            {error && <div className="banner error" style={{ marginBottom: 16 }}>{error}</div>}

            <Card>
                <div className="row" style={{ marginBottom: 12 }}>
                    <div style={{ flex: 1, maxWidth: 320 }}>
                        <TextField label="Programme title" value={program.title}
                            onChange={(v) => run(() => data.upsertProgram({ ...program, title: v }))} />
                    </div>
                    <div style={{ maxWidth: 200 }}>
                        <SelectField
                            label="Pacing" value={program.pacing}
                            onChange={(v) => run(() => data.upsertProgram({ ...program, pacing: v }))}
                            options={[
                                { value: 'selfPaced', label: 'Self-paced' },
                                { value: 'scheduled', label: 'Scheduled' },
                                { value: 'skillGated', label: 'Skill gated' },
                                { value: 'library', label: 'Library (no order)' },
                            ]}
                        />
                    </div>
                </div>

                <div className="tree">
                    {rows.map(({ node, depth }) => {
                        const session = sessions.find((s) => s.id === node.session_id);
                        const isSession = node.session_id !== null;
                        return (
                            <div
                                key={node.id}
                                className={`tree-row ${isSession ? 'is-session' : 'is-group'}`}
                                style={{ marginLeft: depth * 22 }}
                            >
                                <Badge tone={isSession ? 'coral' : 'neutral'}>{levelName(depth)}</Badge>

                                <input
                                    style={{ flex: 1, minWidth: 120, border: 'none', background: 'transparent', padding: '2px 0' }}
                                    value={node.title}
                                    onChange={(e) => run(() => data.upsertProgramNode({
                                        id: node.id, module_id: node.module_id, title: e.target.value,
                                    }).then(() => {}))}
                                />

                                {isSession && (
                                    <span className="small muted">
                                        {session ? session.title : '⚠ missing session'}
                                    </span>
                                )}

                                <div className="tree-controls">
                                <button className="btn icon secondary" disabled={busy}
                                    onClick={() => run(async () => {
                                        const rows = moveSibling(nodes, node.id, 'up');
                                        if (rows.length) await data.reorderProgramNodes(rows);
                                    })} aria-label="Move up">↑</button>
                                <button className="btn icon secondary" disabled={busy}
                                    onClick={() => run(async () => {
                                        const rows = moveSibling(nodes, node.id, 'down');
                                        if (rows.length) await data.reorderProgramNodes(rows);
                                    })} aria-label="Move down">↓</button>

                                {!isSession && (
                                    <button className="btn secondary small" disabled={busy}
                                        onClick={() => run(() => data.upsertProgramNode({
                                            module_id: node.module_id,
                                            parent_id: node.id,
                                            title: `New ${levelName(depth + 1).toLowerCase()}`,
                                            sort_order: nextSortOrder(nodes, node.id),
                                        }).then(() => {}))}>+ child</button>
                                )}

                                {depth > 0 && (
                                    <button className="btn danger small" disabled={busy}
                                        onClick={() => {
                                            const descendants = descendantIds(nodes, node.id);
                                            const message = descendants.length
                                                ? `Delete “${node.title}” and its ${descendants.length} child node(s)?`
                                                : `Delete “${node.title}”?`;
                                            if (window.confirm(message)) {
                                                run(() => data.deleteProgramNode(node.id));
                                            }
                                        }}>Delete</button>
                                )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Card>

            <div style={{ marginTop: 20 }}>
                <Card>
                    <h2>Attach a session</h2>
                    <p className="small muted" style={{ marginTop: 4 }}>
                        Sessions not attached anywhere stay in the standalone Workouts library.
                    </p>
                    {unattached.length === 0 ? (
                        <p className="small muted" style={{ marginTop: 12 }}>
                            Every session is attached to the tree.
                        </p>
                    ) : (
                        <div className="list" style={{ marginTop: 12 }}>
                            {unattached.map((session) => (
                                <div key={session.id} className="list-row">
                                    <div style={{ flex: 1 }}>{session.title}</div>
                                    <AttachControl
                                        groups={rows.filter((r) => r.node.session_id === null)}
                                        onAttach={(parentId) => run(() => data.upsertProgramNode({
                                            module_id: session.module_id,
                                            parent_id: parentId,
                                            title: session.title,
                                            session_id: session.id,
                                            sort_order: nextSortOrder(nodes, parentId),
                                        }).then(() => {}))}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </Card>
            </div>

            {rows.length === 1 && (
                <Empty title="Just the root so far"
                    hint="Add a child to create your first phase, then weeks and days beneath it." />
            )}
        </>
    );
}

function AttachControl({ groups, onAttach }: {
    groups: { node: ProgramNode; depth: number }[];
    onAttach: (parentId: Uuid) => void;
}) {
    const [parent, setParent] = useState('');
    return (
        <div className="row-tight">
            <select value={parent} onChange={(e) => setParent(e.target.value)} style={{ maxWidth: 220 }}>
                <option value="">Choose a parent…</option>
                {groups.map(({ node, depth }) => (
                    <option key={node.id} value={node.id}>
                        {'— '.repeat(depth)}{node.title}
                    </option>
                ))}
            </select>
            <button className="btn small" disabled={!parent} onClick={() => onAttach(parent)}>Attach</button>
        </div>
    );
}
